import { AuthStorage } from "utils/auth-storage";
import { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import styles from "./pdfViewer.module.css";
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import { Button, Tooltip, Select, message, Breadcrumb, Modal } from "antd";
import { HilyteService } from "apis/Services/HilyteService";
import { useParams } from "react-router-dom";

// Correct worker file path
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- LazyThumbnail Component ---
function LazyThumbnail({
  pageNumber,
  file,
  isActive,
  onClick,
  scrollContainerRef,
  pageData,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const thumbnailRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        root: scrollContainer,
        rootMargin: "500px 0px",
        threshold: 0.01,
      },
    );

    if (thumbnailRef.current) {
      observer.observe(thumbnailRef.current);
    }

    return () => {
      if (thumbnailRef.current) {
        observer.unobserve(thumbnailRef.current);
      }
    };
  }, [scrollContainerRef, pageNumber]);

  return (
    <div
      ref={thumbnailRef}
      className={`${styles.thumb} ${isActive ? styles.active : ""}`}
      onClick={onClick}
    >
      <div className={styles.thumbLabel}>
        {pageData?.name || `Page ${pageNumber}`}
      </div>
    </div>
  );
}

// --- Main Component ---
export default function PreviewPDF({
  pdfID,
  data,
  file,
  setData,
  setRequestsData,
  findSubmittalsPrompt,
  onBack,
  setFileUrl,
  setIsOverwrite,
  setIsCursorMode,
  setExistingSections,
  tableState,
}) {
  const { company_id } = useParams();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);

  // Panning States
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Selection/Crop States
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });
  const [pdfPageDimensions, setPdfPageDimensions] = useState(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingPDF, setAnalyzingPDF] = useState(false);
  const [pagesData, setPagesData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  const [fileSections, setFileSections] = useState([]);
  const [dividedSections, setDividedSections] = useState([]);

  const thumbsScrollRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const pageRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const baseWidth = 850;
  const pageWidth = baseWidth * zoomLevel;

  // --- Helper: Fetch the PDF from the URL and return an ArrayBuffer ---
  const fetchPdfArrayBuffer = async () => {
    if (!file) throw new Error("No PDF URL available");
    const response = await fetch(file);
    if (!response.ok)
      throw new Error(`Failed to fetch PDF: ${response.statusText}`);
    return response.arrayBuffer();
  };

  // --- Helper: Fetch the PDF URL and return it as a File object ---
  const fetchPdfAsFile = async (fileName = "document.pdf") => {
    const arrayBuffer = await fetchPdfArrayBuffer();
    const blob = new Blob([arrayBuffer], { type: "application/pdf" });
    return new File([blob], fileName, { type: "application/pdf" });
  };

  function onLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
    setError(null);
  }

  function onLoadError(error) {
    console.error("Error loading PDF:", error);
    setError("Failed to load PDF. Please try again.");
  }

  function onPageLoadSuccess(page) {
    setPageLoading(false);
    setPdfPageDimensions({
      width: page.originalWidth,
      height: page.originalHeight,
    });
  }

  function changePage(newPage) {
    if (pageNumber === newPage) return;
    setPageLoading(true);
    setPageNumber(newPage);
    setSelectionBox(null);
    if (pdfContainerRef.current) {
      pdfContainerRef.current.scrollTop = 0;
      pdfContainerRef.current.scrollLeft = 0;
    }
  }

  function handleZoomIn() {
    setZoomLevel((prev) => Math.min(prev + 0.25, 4));
  }

  function handleZoomOut() {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  }

  const getPDF = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    try {
      const res = await HilyteService.getPDF({
        company: company_id,
        loginEmail,
        code,
        pdfID,
      });

      if (res?.presigned_url) {
        setFileUrl(res.presigned_url);
      } else {
        message.error("Failed to load PDF URL");
      }

      if (res?.json_submittals) {
        // ✅ FIXED: stored submittals go through setData (preserves isPending from backend)
        // NOT setRequestsData (which would force everything to isPending:true)
        setData(res.json_submittals);
      } else {
        message.error("Failed to load submittals");
      }

      if (res?.json_sections) {
        setExistingSections(res?.json_sections);
        if (
          res.json_sections.divided &&
          Array.isArray(res.json_sections.divided)
        ) {
          setFileSections(
            (res.json_sections.original || []).map((s) => ({
              ...s,
              page: Number(s.page),
            })),
          );
          setDividedSections(res.json_sections.divided);
        } else if (Array.isArray(res.json_sections)) {
          setFileSections(res.json_sections);
        } else {
          message.error("Invalid PDF sections format");
        }
      } else {
        message.error("Failed to load PDF Sections");
      }
    } catch (err) {
      console.error("Error fetching PDF:", err);
      message.error("Failed to load PDF");
      setError("Failed to load PDF");
    }
  };

  const getSinglePDFStatus = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    try {
      const res = await HilyteService.getSinglePDFStatus({
        company: company_id,
        loginEmail,
        code,
        pdfID,
      });

      return res;
    } catch (err) {
      console.error("Error fetching PDF status:", err);
      return null;
    }
  };

  useEffect(() => {
    if (pdfID) {
      getPDF();
    }
  }, [pdfID]);

  const [isPolling, setIsPolling] = useState(false);

  // --- Polling useEffect ---
  useEffect(() => {
    if (!isPolling) return;

    const intervalId = setInterval(async () => {
      const statusRes = await getSinglePDFStatus();

      if (statusRes?.submittals_status === "Processed") {
        clearInterval(intervalId);
        setIsPolling(false);
        message.destroy();
        await getPDF();
        setAnalyzingPDF(false);
        message.success("PDF analyzed successfully!");
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [isPolling]);

  // --- Analyze Full PDF ---
  const handleAnalyzePDF = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    console.log(data, tableState, "!?!?");

    setIsCursorMode(false);

    const isDataEmpty = !data || Object.keys(data).length === 0;
    const isTableStateEmpty =
      !tableState || Object.keys(tableState).length === 0;

    if (isDataEmpty && isTableStateEmpty) {
      await triggerFullPDFAnalysis("1", loginEmail, code);
      return;
    }

    Modal.confirm({
      title: "Overwrite existing results?",
      content:
        "Do you want to overwrite existing results with submittals, or keep both? (Keeping both might create duplicates)",
      okText: "Overwrite",
      cancelText: "Keep Both",
      onOk: async () => {
        await triggerFullPDFAnalysis("1", loginEmail, code);
      },
      onCancel: async () => {
        await triggerFullPDFAnalysis("0", loginEmail, code);
      },
    });
  };

  const triggerFullPDFAnalysis = async (overwriteValue, loginEmail, code) => {
    setIsOverwrite(overwriteValue);
    setAnalyzingPDF(true);
    const loadingMessage = message.loading("Submitting PDF for analysis...", 0);

    try {
      HilyteService.extractSubmittalsFromPDF({
        loginEmail,
        code,
        company: company_id,
        pdfID,
        data: {
          prompt: findSubmittalsPrompt,
          overwrite: overwriteValue,
        },
      });

      loadingMessage();
      message.loading("Processing PDF... This may take a moment.", 0);
      setIsPolling(true);
    } catch (err) {
      loadingMessage();
      console.error("Error analyzing PDF:", err);
      message.error("Failed to analyze PDF");
      setAnalyzingPDF(false);
    }
  };

  // --- Mouse Handlers ---
  function handleMouseDown(e) {
    if (analyzing) return;

    if (isSelectionMode) {
      if (!pageRef.current) return;

      const rect = pageRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setIsSelecting(true);
      setSelectionStart({ x, y });
      setSelectionBox({ x, y, width: 0, height: 0 });
      e.preventDefault();
    } else {
      if (zoomLevel > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX + pdfContainerRef.current.scrollLeft,
          y: e.clientY + pdfContainerRef.current.scrollTop,
        });
        e.preventDefault();
      }
    }
  }

  function handleMouseMove(e) {
    if (analyzing) return;

    if (isSelectionMode && isSelecting) {
      if (!pageRef.current) return;

      const rect = pageRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      const width = currentX - selectionStart.x;
      const height = currentY - selectionStart.y;

      setSelectionBox({
        x: width > 0 ? selectionStart.x : currentX,
        y: height > 0 ? selectionStart.y : currentY,
        width: Math.abs(width),
        height: Math.abs(height),
      });
    } else if (isDragging && pdfContainerRef.current) {
      const dx = dragStart.x - e.clientX;
      const dy = dragStart.y - e.clientY;
      pdfContainerRef.current.scrollLeft = dx;
      pdfContainerRef.current.scrollTop = dy;
    }
  }

  async function handleMouseUp() {
    if (analyzing) return;

    if (isSelectionMode && isSelecting) {
      setIsSelecting(false);

      if (
        selectionBox &&
        (selectionBox.width < 10 || selectionBox.height < 10)
      ) {
        setSelectionBox(null);
        return;
      }

      if (selectionBox) {
        await extractSelectedAreaAsPDF();
        setSelectionBox(null);
      }
    }
    setIsDragging(false);
  }

  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging || isSelecting) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, isSelecting, selectionBox, dragStart]);

  // --- Logic to Crop PDF ---
  const extractSelectedAreaAsPDF = async () => {
    if (!selectionBox || !pdfPageDimensions || !file) return;

    setAnalyzing(true);
    const loadingMessage = message.loading("Cropping and analyzing area...", 0);

    try {
      const PDFLib = await import("pdf-lib");
      const { PDFDocument, degrees } = PDFLib;

      const arrayBuffer = await fetchPdfArrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      const pageIndex = pageNumber - 1;
      const sourcePage = pdfDoc.getPage(pageIndex);

      const rotationAngle = sourcePage.getRotation().angle;
      const { width: rawWidth, height: rawHeight } = sourcePage.getSize();

      const isRotated = rotationAngle === 90 || rotationAngle === 270;
      const pdfVisualWidth = isRotated ? rawHeight : rawWidth;
      const scaleFactor = pdfVisualWidth / pageWidth;

      const selX = selectionBox.x * scaleFactor;
      const selY = selectionBox.y * scaleFactor;
      const selW = selectionBox.width * scaleFactor;
      const selH = selectionBox.height * scaleFactor;

      const isFullPage =
        Math.abs(selW - pdfVisualWidth) < pdfVisualWidth * 0.01 &&
        Math.abs(selH - (isRotated ? rawWidth : rawHeight)) < rawHeight * 0.01;

      if (isFullPage) {
        loadingMessage();
        await analyzeCurrentPage();
        return;
      }

      let cropX, cropY, cropW, cropH;

      if (rotationAngle === 0) {
        cropX = selX;
        cropY = rawHeight - selY - selH;
        cropW = selW;
        cropH = selH;
      } else if (rotationAngle === 90) {
        cropX = selY;
        cropY = selX;
        cropW = selH;
        cropH = selW;
      } else if (rotationAngle === 180) {
        cropX = rawWidth - selX - selW;
        cropY = selY;
        cropW = selW;
        cropH = selH;
      } else if (rotationAngle === 270) {
        cropX = rawWidth - selY - selH;
        cropY = rawHeight - selX - selW;
        cropW = selH;
        cropH = selW;
      }

      const newPdfDoc = await PDFDocument.create();
      const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [pageIndex]);

      const finalX = Math.max(0, cropX);
      const finalY = Math.max(0, cropY);

      copiedPage.setCropBox(finalX, finalY, cropW, cropH);
      copiedPage.setMediaBox(finalX, finalY, cropW, cropH);

      if (rotationAngle !== 0) {
        copiedPage.setRotation(degrees(0));
      }

      newPdfDoc.addPage(copiedPage);

      const pdfBytes = await newPdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const croppedFile = new File([blob], `crop-page-${pageNumber}.pdf`, {
        type: "application/pdf",
      });

      loadingMessage();
      await extractPortionSubmittals(croppedFile);
    } catch (err) {
      loadingMessage();
      console.error("Error cropping PDF:", err);
      message.error("Failed to process selected area");
      setAnalyzing(false);
    }
  };

  const analyzeCurrentPage = async () => {
    if (!file || !pageNumber) {
      message.error("No page selected");
      return;
    }
    // ✅ Don't set analyzing here — extractPortionSubmittals manages it
    try {
      const pdfFile = await fetchPdfAsFile("document.pdf");
      const singlePagePdf = await extractSinglePageAsPDF(pdfFile, pageNumber);
      await extractPortionSubmittals(singlePagePdf);
    } catch (err) {
      console.error(err);
      message.error("Failed to analyze page");
      setAnalyzing(false); // only set false here if extractPortionSubmittals didn't get to run
    }
  };

  const extractSinglePageAsPDF = async (pdfFile, pageNum) => {
    const originalArrayBuffer = await pdfFile.arrayBuffer();
    const arrayBufferCopy = originalArrayBuffer.slice(0);
    const PDFLib = await import("pdf-lib");
    const { PDFDocument } = PDFLib;
    const pdfDoc = await PDFDocument.load(arrayBufferCopy);
    const newPdfDoc = await PDFDocument.create();

    const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [pageNum - 1]);
    newPdfDoc.addPage(copiedPage);

    const pdfBytes = await newPdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    return new File([blob], `page-${pageNum}.pdf`, { type: "application/pdf" });
  };

  const extractPortionSubmittals = async (pdfFile) => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    setIsCursorMode(true);

    const formData = new FormData();
    formData.append("file", pdfFile);
    formData.append("images", "");
    formData.append("prompt", findSubmittalsPrompt);
    formData.append("text_input", "");

    const loadingMessage = message.loading("Extracting submittals...", 0);

    try {
      const res = await HilyteService.extractPortionSubmittals({
        company: company_id,
        loginEmail,
        code,
        formData,
      });

      loadingMessage();

      if (res?.ok) {
        const responseJson = await res.json();

        // Add this log to see the actual shape
        console.log("extractPortionSubmittals response:", responseJson);

        // The response might BE the data directly, not nested under "raw"
        const raw = responseJson?.raw || responseJson || {};
        setRequestsData(raw);

        const submittalsCount = Object.values(raw).reduce(
          (total, subdivisions) =>
            total +
            Object.values(subdivisions || {}).reduce(
              (subTotal, items) => subTotal + (items?.length || 0),
              0,
            ),
          0,
        );

        if (submittalsCount > 0) {
          message.success(
            `${submittalsCount} submittal${
              submittalsCount > 1 ? "s" : ""
            } extracted successfully!`,
          );
        } else {
          message.info("No submittals were found in this PDF.");
        }
      } else {
        message.error("Failed to extract submittals.");
      }
    } catch (err) {
      loadingMessage();
      console.error("Error uploading file:", err);
      message.error("Error extracting submittals.");
    } finally {
      setAnalyzing(false);
    }
  };

  const filteredPages = numPages
    ? Array.from(new Array(numPages), (_, i) => i + 1).filter((pageNum) => {
        if (selectedCategory === "all") return true;
        return pagesData[pageNum]?.category === selectedCategory;
      })
    : [];

  const containerCursor = analyzing
    ? "default"
    : isSelectionMode
      ? "crosshair"
      : isDragging
        ? "grabbing"
        : zoomLevel > 1
          ? "grab"
          : "default";

  const handleBack = () => {
    setRequestsData({});
    onBack();
  };

  return (
    <div className={styles.container}>
      {error ? (
        <div>
          <Breadcrumb style={{ marginBottom: 12 }}>
            <Breadcrumb.Item>
              <a onClick={handleBack}>Your Uploaded Files</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Analyze</Breadcrumb.Item>
          </Breadcrumb>

          <div style={{ color: "red", padding: "10px", textAlign: "center" }}>
            {error}
          </div>
        </div>
      ) : (
        <div>
          <Breadcrumb style={{ marginBottom: 12 }}>
            <Breadcrumb.Item>
              <a onClick={handleBack}>Your Uploaded Files</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Analyze</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      )}
      <div className={styles.wrapper}>
        <div className={styles.mainView}>
          {numPages && (
            <div className={styles.controlsWrapper}>
              <div className={styles.navigationControls}>
                <Tooltip title="Previous Page">
                  <Button
                    disabled={pageNumber <= 1}
                    type="primary"
                    shape="circle"
                    onClick={() => changePage(pageNumber - 1)}
                  >
                    <LeftOutlined />
                  </Button>
                </Tooltip>
                <span>
                  Page {pageNumber} of {numPages}
                </span>
                <Tooltip title="Next Page">
                  <Button
                    type="primary"
                    shape="circle"
                    disabled={pageNumber >= numPages}
                    onClick={() => changePage(pageNumber + 1)}
                  >
                    <RightOutlined />
                  </Button>
                </Tooltip>
              </div>

              <div
                style={{
                  display: "inline-flex",
                  alignItems: "stretch",
                  border: "1px solid #1677ff",
                  borderRadius: 8,
                  overflow: "hidden",
                  opacity:
                    analyzing || isSelectionMode || analyzingPDF ? 0.5 : 1,
                  pointerEvents:
                    analyzing || isSelectionMode || analyzingPDF
                      ? "none"
                      : "auto",
                  boxShadow: "0 1px 3px rgba(22,119,255,0.15)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    background: "#1677ff",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {analyzing || analyzingPDF ? (
                    <LoadingOutlined style={{ fontSize: 14 }} />
                  ) : (
                    <AutoFixHighIcon style={{ fontSize: 16 }} />
                  )}
                </div>

                <Tooltip title="Analyze entire page">
                  <button
                    onClick={analyzeCurrentPage}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "0 12px",
                      height: 32,
                      background: "#fff",
                      color: "#1677ff",
                      border: "none",
                      borderRight: "1px solid #d0e3ff",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 500,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f0f7ff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                  >
                    Page
                  </button>
                </Tooltip>

                <Tooltip title="Analyze entire PDF and extract all submittals">
                  <button
                    onClick={handleAnalyzePDF}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "0 12px",
                      height: 32,
                      background: "#fff",
                      color: "#1677ff",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 500,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f0f7ff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                  >
                    PDF
                  </button>
                </Tooltip>
              </div>

              <div className={styles.filterControls}>
                <Select
                  value={
                    categories.length === 0
                      ? "All Categories"
                      : selectedCategory
                  }
                  onChange={setSelectedCategory}
                  style={{ width: 250 }}
                  disabled={categories.length === 0}
                  placeholder="Filter by category"
                  popupMatchSelectWidth={false}
                  showSearch
                >
                  {categories.map((cat) => (
                    <Select.Option key={cat} value={cat}>
                      {cat === "all"
                        ? "All Categories"
                        : `${cat} (${
                            Object.values(pagesData).filter(
                              (p) => p.category === cat,
                            ).length
                          })`}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <Tooltip
                title={
                  isSelectionMode
                    ? "Cancel Selection"
                    : "Select Area to Analyze"
                }
              >
                <Button
                  type={isSelectionMode ? "default" : "primary"}
                  danger={isSelectionMode}
                  icon={
                    analyzing ? (
                      <LoadingOutlined />
                    ) : (
                      <HighlightAltIcon style={{ fontSize: "1.3rem" }} />
                    )
                  }
                  onClick={() => {
                    setIsSelectionMode(!isSelectionMode);
                    setSelectionBox(null);
                  }}
                  disabled={analyzing || analyzingPDF}
                >
                  {isSelectionMode ? "Cancel" : "Cursor"}
                </Button>
              </Tooltip>

              <div className={styles.zoomControls}>
                <Tooltip title="Zoom Out">
                  <Button
                    type="primary"
                    shape="circle"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                  >
                    <ZoomOutOutlined />
                  </Button>
                </Tooltip>
                <span className={styles.zoomLevel}>
                  {Math.round(zoomLevel * 100)}%
                </span>
                <Tooltip title="Zoom In">
                  <Button
                    type="primary"
                    shape="circle"
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 4}
                  >
                    <ZoomInOutlined />
                  </Button>
                </Tooltip>
              </div>
            </div>
          )}

          {(analyzing || analyzingPDF) && (
            <div className={`${styles.analysisOverlay}`}>
              <div className={styles.analysisContent}>
                <LoadingOutlined
                  style={{ fontSize: 40, color: "#1890ff", marginBottom: 16 }}
                />
                <div className={styles.analysisText}>
                  {analyzingPDF ? "Analyzing PDF..." : "Analyzing..."}
                </div>
              </div>
            </div>
          )}

          {/* PDF Container */}
          <div
            className={`${styles.pdfContainer} ${
              zoomLevel <= 1 ? styles.centered : ""
            }`}
            ref={pdfContainerRef}
            onMouseDown={handleMouseDown}
            style={{
              cursor: containerCursor,
              position: "relative",
            }}
          >
            {pageLoading && (
              <div className={styles.spinner}>
                <div className={styles.spinnerCircle}></div>
              </div>
            )}

            <div
              className={styles.pageWrapper}
              style={{ width: pageWidth, position: "relative" }}
              ref={pageRef}
            >
              {/* SELECTION OVERLAY */}
              {selectionBox && (
                <div
                  style={{
                    position: "absolute",
                    border: "2px solid #1890ff",
                    backgroundColor: "rgba(24, 144, 255, 0.2)",
                    left: selectionBox.x,
                    top: selectionBox.y,
                    width: selectionBox.width,
                    height: selectionBox.height,
                    zIndex: 10,
                    pointerEvents: "none",
                  }}
                />
              )}

              <Document
                file={file}
                onLoadSuccess={onLoadSuccess}
                onLoadError={onLoadError}
                loading={
                  <div className={styles.spinner}>
                    <div className={styles.spinnerCircle}></div>
                    <div>Loading PDF...</div>
                  </div>
                }
              >
                {numPages && (
                  <Page
                    pageNumber={pageNumber}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={pageWidth}
                    onLoadSuccess={onPageLoadSuccess}
                    loading={
                      <div className={styles.pagePlaceholder}>
                        <div className={styles.spinnerCircle}></div>
                      </div>
                    }
                  />
                )}
              </Document>
            </div>
          </div>
        </div>

        {/* Thumbnails */}
        {numPages && (
          <div className={styles.thumbs}>
            <div className={styles.thumbsHeader}>
              Pages {selectedCategory !== "all" && `(${selectedCategory})`}
            </div>
            <div className={styles.thumbsScroll} ref={thumbsScrollRef}>
              {filteredPages.map((pageNum) => (
                <LazyThumbnail
                  key={pageNum}
                  pageNumber={pageNum}
                  file={file}
                  isActive={pageNumber === pageNum}
                  onClick={() => changePage(pageNum)}
                  scrollContainerRef={thumbsScrollRef}
                  pageData={fileSections.find(
                    (s) => Number(s.page) === pageNum,
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
